import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { FRESHDESK, OnboardingFamilyConstants as ui } from '../../Config/UIConstants';
import { CommonTextInput } from '../../SharedComponents/CommonTextInput';
import { FullScreenModalContainer } from '../../SharedComponents/FullScreenModalContainer';
import { ModalActionButtonBar } from '../../SharedComponents/ModalActionButtonBar';
import { SuggestionTextInput } from '../../SharedComponents/SuggestionTextInput';
import { IInviteInformation } from '../../Stores/OnboardingStore';
import { Routes } from '../../_controllers/RouteController';
import { ApplicationStore } from '../../_stores/ApplicationStore';
import { UserStore } from '../../_stores/UserStore';
import { colors } from '../../styles/colors';
import { largeShadow } from '../../styles/styleObjects';
import { Url } from '../../util/Url';
import { validateEmail } from '../../util/validateEmail';
import parseSearchString = Url.parseSearchString;

// interface IRouteProps {
// }
export const RETURN_URL = 'returnUrl';
export const NEW_FAMILY_ID = 'newFamilyId';

interface IState {
    invites: Array<IInviteInformation>;
    familyName: string;
    isAdding: boolean;
}

interface IProps {
    store: ApplicationStore;
}

@inject('store')
@observer
export class AddFamilyPage extends React.Component<RouteComponentProps<any> & IProps, IState> {

    public state: IState = {
        invites: Array(4)
            .fill(0)
            .map(() => ({ firstName: '', email: '' })),
        familyName: '',
        isAdding: false
    };

    private createFamily = async (): Promise<void> => {
        this.setState({
            isAdding: true
        });
        const serverFamilyPromise = this.props.store.userFamilyStore
            .createFamily(this.state.familyName, this.state.invites);

        const parsedQuery = parseSearchString(this.props.location.search);
        let returnUrl: string | undefined = parsedQuery[RETURN_URL];
        if (returnUrl) {
            if (returnUrl.indexOf('?') < 0) {
                returnUrl += '?';
            }

            const serverFamily = await serverFamilyPromise;
            returnUrl += NEW_FAMILY_ID + '=' + encodeURIComponent(serverFamily.id.toString());

            this.props.history.push(returnUrl);
            return;
        }

        await serverFamilyPromise;
        this.props.history.push(Routes.FAMILIES);
    }

    private renderFamilyInviteInputs(index: number) {
        if (!this.props.store.userFamilyStore || !this.props.store.userFamilyStore.familiesAreLoaded) {
            return null;
        }
        const rowInfo = this.state.invites[index];
        const placeholder = index > 0 ? ui.OPTIONAL_PLACEHOLDER : ui.REQUIRED_PLACEHOLDER;
        return (
            <div style={styles.inputRowContainer}>
                {this.renderSingleInput('firstName', placeholder, rowInfo, index)}
                {this.renderSingleInput('email', placeholder, rowInfo, index)}
            </div>
        );
    }

    private renderSingleInput(type: 'firstName' | 'email', placeholder: string, rowInfo: IInviteInformation, index: number) {
        const userFilterKey = type === 'firstName' ? 'first_name' : 'email';
        const suggestions =
            rowInfo[type].length > 0
                ? this.props.store.userFamilyStore.users
                      .filter(user => user[userFilterKey].toLocaleLowerCase().includes(rowInfo[type].toLocaleLowerCase()))
                      .map(user => ({ display: `${UserStore.getPublicUserFullName(user)} (${user.email})`, value: user }))
                : [];
        return (
            <SuggestionTextInput
                header={type === 'firstName' ? ui.FIRST_NAME_HEADER : ui.EMAIL_HEADER}
                placeholder={placeholder}
                value={rowInfo[type]}
                onChangeText={(text: string) => {
                    let state = { ...this.state };
                    state.invites[index][type] = text;
                    this.setState({ invites: state.invites });
                }}
                suggestions={suggestions}
                containerStyle={styles.inputContainer}
                onPressSuggestion={({ first_name, email }) => {
                    const invites = [...this.state.invites];
                    invites[index].firstName = first_name;
                    invites[index].email = email;

                    this.setState({ invites });
                }}
            />
        );
    }

    public render() {
        let confirmContent: React.ReactNode;
        if (this.state.isAdding) {
            confirmContent = <div className="spinner-border" style={styles.spinner} role="status">
                <span className="sr-only">Loading...</span>
            </div>;
        } else {
            confirmContent = 'Create';
        }
        const isConfirmEnabled = (!this.state.isAdding) &&
            this.state.familyName === '' ||
            !validateEmail(this.state.invites[0].email) ||
            this.state.invites[0].firstName === '';

        return (
            <FullScreenModalContainer justifyContent="space-between" alignItems="center" {...this.props} onPressExit={() => this.props.history.push(Routes.FAMILIES)}>
                <p style={styles.headerText}>Create Family</p>
                <div style={styles.contentContainer}>
                        <CommonTextInput
                            value={this.state.familyName}
                            containerStyle={styles.topInput}
                            onChangeText={(text: string) => {
                                this.setState({ familyName: text });
                            }}
                            header={ui.FAMILY_NAME_HEADER}
                            placeholder={ui.REQUIRED_PLACEHOLDER}
                            helpLink={FRESHDESK.MANAGE_FAMILY_GROUPINGS}
                        />
                    <p style={styles.subHeader}>{ui.FAMILY_MEMBERS}</p>
                    <p style={styles.topSubText}>{ui.TOP_SUB_TEXT}</p>
                    <p style={styles.bottomSubText}>{ui.BOTTOM_SUB_TEXT}</p>
                    <div style={styles.inputsContainer}>
                        {this.renderFamilyInviteInputs(0)}
                        {this.renderFamilyInviteInputs(1)}
                        {this.renderFamilyInviteInputs(2)}
                        {this.renderFamilyInviteInputs(3)}
                    </div>
                </div>

                <ModalActionButtonBar
                    confirmContent={confirmContent}
                    confirmOnClick={() => this.createFamily()}
                    altPadding={25}
                    confirmDisabled={isConfirmEnabled}
                />
            </FullScreenModalContainer>
        );
    }
}

const styles = {
    inputRowContainer: { display: 'flex', flexDirection: 'row' } as React.CSSProperties,
    rightInputsContainer: {
        display: 'flex',
        flexDirection: 'column',
        height: 300,
        width: 400,
        marginLeft: 15
    } as React.CSSProperties,
    leftInputsContainer: {
        display: 'flex',
        flexDirection: 'column',
        height: 300,
        width: 400,
        marginRight: 15
    } as React.CSSProperties,
    inputsContainer: {
        display: 'flex',
        flexDirection: 'column',
        width: 800
    } as React.CSSProperties,
    bottomSubText: {
        fontSize: 18,
        marginBottom: 15
    },
    topSubText: {
        fontSize: 18,
        marginBottom: 3
    },
    header: {
        marginBottom: 10,
        fontSize: 18
    } as React.CSSProperties,
    subHeader: {
        fontSize: 22,
        marginBottom: 10,
        marginTop: 10,
        color: 'black',
        fontWeight: 900
    } as React.CSSProperties,
    inputContainer: {
        width: 400,
        marginLeft: 10,
        marginRight: 10,
        // ToDo: Make form responsive. Previous setting: transform: window.innerHeight < 725 ? `scale(0.75)` : ''
    },
    topInput: {
        width: 400,
        height: 75,
        marginBottom: 15,
        backgroundColor: 'white',
        boxShadow: largeShadow
    },
    headerText: {
        fontSize: 25,
        color: colors.DARK_TEXT,
        marginTop: 50,
        marginBottom: 30
    },
    contentContainer: {
        display: 'flex',
        width: '100%',
        height: '50%',
        flex: 1,
        flexDirection: 'column',
        alignItems: 'center',
        // ToDo: Make form responsive. Previous setting: transform: window.innerHeight < 800 ? `scale(0.8)` : ''
    } as React.CSSProperties,
    bottomContainer: {
        height: 150,
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column'
    } as React.CSSProperties,
    buttonText: {
        fontSize: 14,
        fontWeight: 200,
        letterSpacing: 1
    } as React.CSSProperties,
    buttonContainer: {
        height: 48,
        width: 180
    },
    activityIndicator: {
        width: 80,
        height: 15,
        marginTop: 35
    },
    spinner: {
        color: '#ffffff40'
    } as React.CSSProperties
};



// WEBPACK FOOTER //
// ./src/Pages/FamiliesPage/AddFamilyPage.tsx